import { useEffect } from 'react';
import { gql } from '@apollo/client';
import { Link, useSearchParams } from 'react-router';
import { useQuery } from '@apollo/client/react';
import { EmptyState } from '../components/EmptyState';
import { anyRunInFlight, cdnHeadline } from '../lib/cdnHeadline';
import { useUrlState } from '../lib/useUrlState';
import { RefreshButton } from '../components/RefreshButton';

/**
 * CDN egress cost — what DO Spaces traffic actually costs.
 *
 * Read-only over the `cdn_cost` database the scheduler's `cdn-cost` job fills. The
 * predecessor to this page died with the legacy devops-controller pod on 2026-07-23
 * and nobody noticed for 34 days, so "is this data actually fresh?" is answered on the
 * page itself rather than assumed — see the freshness line under the headline.
 *
 * Design + why the pipeline was rebuilt: `docs/cdn-cost.md`.
 */

const CDN_COST = gql`
  query CdnCost($days: Int!) {
    cdnCostSummary(days: $days) {
      configured
      bytesSent
      requestCount
      cdnBytes
      originBytes
      ratePerGb
      estimatedCost
      days
      ingestedThrough
    }
    cdnDailyEgress(days: $days) {
      reportDate
      source
      bytesSent
      requestCount
      uniqueKeys
      isFinal
    }
    cdnTopObjects(days: $days, limit: 20) {
      reportDate
      source
      objectKey
      bytesSent
      requestCount
    }
  }
`;

/**
 * Is a run in flight RIGHT NOW? The page cannot tell "loading" from "this window has
 * not been reached yet" out of its own totals (QC e-112), and the summary exposes no
 * run state. `jobRuns` reads are open to any authenticated staff — the same posture as
 * `cdnCostSummary` — so this adds no permission surface.
 *
 * Deliberately its OWN query (review F-6): `jobRuns` is a non-null list, so asking for
 * it inside CDN_COST nulls the whole response when the jobs subsystem throws — a
 * missing `job_run` table on a fresh database would make the cost page render
 * "Couldn't load CDN egress" and blame `cdn_cost`. Separated, a jobs failure only
 * makes run state UNKNOWN, and the headline degrades to the watermark-based states.
 *
 * LIMIT 5, not 1 — see `anyRunInFlight`: a `skipped` row from a tick that could not
 * take the lock is NEWER than the run it collided with.
 */
const CDN_JOB_RUNS = gql`
  query CdnCostJobRuns {
    jobRuns(jobName: "cdn-cost", limit: 5) {
      id
      status
    }
  }
`;

interface Summary {
  configured: boolean;
  bytesSent: number;
  requestCount: number;
  cdnBytes: number;
  originBytes: number;
  ratePerGb: number;
  estimatedCost: number;
  days: number;
  ingestedThrough: string | null;
}
interface DailyRow {
  reportDate: string;
  source: string;
  bytesSent: number;
  requestCount: number;
  uniqueKeys: number | null;
  isFinal: boolean;
}
interface JobRun {
  id: string;
  status: string;
}
interface TopObject {
  reportDate: string;
  source: string;
  objectKey: string;
  bytesSent: number;
  requestCount: number;
}

const TB = 1024 ** 4;
const GB = 1024 ** 3;

function bytes(n: number): string {
  if (n >= TB) return `${(n / TB).toFixed(2)} TB`;
  if (n >= GB) return `${(n / GB).toFixed(1)} GB`;
  return `${(n / 1024 ** 2).toFixed(0)} MB`;
}
const usd = (n: number): string =>
  n >= 100 ? `$${Math.round(n).toLocaleString()}` : `$${n.toFixed(2)}`;

const RANGES = [7, 30, 90] as const;
const DEFAULT_DAYS = 30;

// Same shape as every other page's Stat (VpnPeersPage / ClustersPage / …). `.label`
// and `.value` must be BLOCK elements — globals.css stacks them, and hand-rolled
// inline spans collapse onto one line ("Egress37.43 TB").
function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="stat">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
    </div>
  );
}

export default function CdnCostPage() {
  // Range lives in the query string, so a filtered view survives a refresh and can be
  // pasted to someone else. `useUrlState` is the house helper — it clears the param
  // when the value equals the default, keeping the common URL clean.
  //
  // Read the parameter RAW rather than through `useUrlNumber`: that helper coerces
  // anything non-positive or non-numeric to the default before the page sees it, so
  // `?days=abc`, `?days=0` and `?days=-5` arrived indistinguishable from no parameter
  // at all and the bogus text stayed in the address bar. Only the raw string can tell
  // "absent" from "nonsense". The window is a fixed set of three, so validity is
  // membership in RANGES, not "is it a positive number".
  const [searchParams] = useSearchParams();
  const [rawDays, setDays] = useUrlState('days', String(DEFAULT_DAYS), 'days');
  const valid = RANGES.some((r) => String(r) === rawDays);
  const days = valid ? Number(rawDays) : DEFAULT_DAYS;

  // A hand-typed `?days=999` renders the 30-day view, so leaving the parameter in the
  // address bar makes the URL state something the page is not showing — and pasting it
  // to a colleague reproduces the lie rather than the view. Normalise it away; the
  // setter deletes the param at the default and writes with replace, so no history
  // entry and no reload loop.
  useEffect(() => {
    if (!valid) setDays(String(DEFAULT_DAYS));
  }, [valid, setDays]);

  const { data, loading, error, refetch } = useQuery<{
    cdnCostSummary: Summary;
    cdnDailyEgress: DailyRow[];
    cdnTopObjects: TopObject[];
  }>(CDN_COST, {
    // ONE window for both tables. An earlier version capped the object window at 30
    // days while the daily table honoured 90, so selecting "Last 90 days" silently
    // emptied "Heaviest objects" — two tables on one page answering different
    // questions. The cap bought nothing: a settled day keeps only its top objects, so
    // even 90 days is ~36k indexed rows.
    variables: { days },
    fetchPolicy: 'cache-and-network',
  });

  const { data: jobsData } = useQuery<{ jobRuns: JobRun[] }>(CDN_JOB_RUNS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const s = data?.cdnCostSummary;
  const daily = data?.cdnDailyEgress ?? [];
  const top = data?.cdnTopObjects ?? [];

  // Only settled days are comparable — a live day is partial by definition, and
  // projecting from it would understate the month every single time.
  const finalDays = daily.filter((d) => d.isFinal);
  const dayCount = new Set(finalDays.map((d) => d.reportDate)).size;
  const settledBytes = finalDays.reduce((a, d) => a + d.bytesSent, 0);
  const perDay = dayCount > 0 ? settledBytes / dayCount : 0;
  const projectedMonthly = s ? (perDay / GB) * s.ratePerGb * 30 : 0;

  // Configured but empty is NOT "zero traffic" — it is "we don't know yet". A watermark
  // is written by the first ingested log file, so `ingestedThrough === null` means the
  // job has never landed a byte. Rendering "0 MB / $0.00" there states a measurement
  // nobody made, and reads as "the CDN is free this month". The same holds when the
  // window itself is empty: every total sums the same rows the daily table lists.
  const ingested = !!s && s.ingestedThrough !== null;
  const noTotals = !ingested || daily.length === 0;

  // The headline is derived from SYSTEM state — is a run in flight, is there a
  // watermark — and only then from this window. Deriving it from window emptiness
  // alone made the default view claim a first batch was in progress hours after the
  // last run succeeded (QC e-112), because the backfill ingests oldest-first and
  // reaches the recent days last. See `lib/cdnHeadline.ts`.
  // A jobs error is "unknown", never the cost page's error card: `errorPolicy: 'all'`
  // keeps the failure out of `error` here, and no rows simply means no run in flight.
  const runInFlight = anyRunInFlight(jobsData?.jobRuns ?? []);
  // MERGE the window into the current query string rather than replacing it (review
  // F-7) — `days` is this page's only param today, but rebuilding the address from a
  // literal is exactly the URL-state mistake `useUrlState` exists to prevent. A
  // relative `{ search }` also keeps the route out of the link.
  const widenSearch = (n: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('days', String(n));
    return `?${next.toString()}`;
  };

  const headline = cdnHeadline({
    ingestedThrough: s?.ingestedThrough ?? null,
    dailyRowCount: daily.length,
    days,
    runInFlight,
  });

  // F-1's shape on this page, found by running the AC-11 QC spec against an
  // UNCONFIGURED environment (qa has a cost DB, local does not): the refresh only
  // existed on the populated branch, so the two states a person is most likely to be
  // waiting on — the DSN just wired, the query just recovered — were the two with no
  // way to re-ask but F5. ONE control for the query, reused in all three placements so
  // the one-per-query count pin still holds.
  const refreshControl = <RefreshButton refetch={() => refetch().catch(() => undefined)} />;
  const emptyToolbar = (
    <div className="detail-head">
      <span className="spacer" />
      {refreshControl}
    </div>
  );

  if (error) {
    return (
      <div className="page page--wide">
        <h1>CDN cost</h1>
        {emptyToolbar}
        <p className="error">Couldn&apos;t load CDN egress.</p>
        <p className="muted">
          If the error mentions <em>not configured</em>, this environment has no{' '}
          <code>cdn_cost</code> database wired up yet — see <code>docs/cdn-cost.md</code>.
        </p>
      </div>
    );
  }

  if (s && !s.configured) {
    return (
      <div className="page page--wide">
        <h1>CDN cost</h1>
        {emptyToolbar}
        <EmptyState
          icon="question"
          title="Not configured in this environment"
          body="CDN_COST_PG_DSN is unset, so there is no cost database to read. The cdn-cost scheduler job fills it from DO Spaces access logs; see docs/cdn-cost.md for what to provision."
        />
      </div>
    );
  }

  return (
    <div className="page page--wide">
      <h1>CDN cost</h1>
      <p className="muted">
        DO Spaces egress, measured from access logs. Cost is an <strong>estimate</strong>
        {/* Only name a rate once we have one — `?? 0` renders "$0.00/GB" while the
            query is in flight, which reads as a real (and free) rate. */}
        {s ? ` at ${usd(s.ratePerGb)}/GB` : ''} — not an invoice.
      </p>

      {/* `.chip` / `.chip--on` were used here and defined NOWHERE in globals.css, so all
          three ranges rendered as identical bare OS buttons and the selected one was
          invisible — the URL said 90 days while the control said nothing. The house
          button system already has a selected look (`.btn-primary`, brand fill), and
          `aria-pressed` states the selection for a screen reader rather than colour alone. */}
      <div className="filter-row" role="group" aria-label="Egress window">
        {RANGES.map((r) => (
          <button
            key={r}
            type="button"
            className={r === days ? 'btn btn-primary' : 'btn'}
            aria-pressed={r === days}
            onClick={() => setDays(String(r))}
          >
            Last {r} days
          </button>
        ))}
      </div>

      <div className="stat-row">
        <Stat label="Egress" value={s && !noTotals ? bytes(s.bytesSent) : '—'} />
        <Stat label="Est. cost" value={s && !noTotals ? usd(s.estimatedCost) : '—'} />
        <Stat label="Run rate / mo" value={dayCount > 0 ? usd(projectedMonthly) : '—'} />
        <Stat
          label="Edge share"
          value={
            s && !noTotals && s.bytesSent > 0
              ? `${Math.round((s.cdnBytes / s.bytesSent) * 100)}%`
              : '—'
          }
        />
      </div>

      {/*
        Freshness is stated, never assumed. The pipeline this replaced stopped for 34
        days behind a green health check; a cost page that silently shows month-old
        totals is the same failure wearing a different hat.
      */}
      <p className="muted">
        {headline.text}
        {headline.widenTo !== undefined && (
          <>
            {' '}
            {/* A real link, not a bare button with a class defined nowhere (the
                `.chip` mistake this file already paid for): it is keyboard reachable,
                middle-clickable and pasteable, and it puts the widened window in the
                URL — the same state the range buttons write. */}
            <Link to={{ search: widenSearch(headline.widenTo) }}>
              widen to {headline.widenTo} days
            </Link>
          </>
        )}
        {dayCount > 0 && ` · run rate from ${dayCount} settled day${dayCount === 1 ? '' : 's'}`}
      </p>

      <div className="detail-head">
        <h2>Daily egress</h2>
        <span className="spacer" />
        {refreshControl}
      </div>
      {daily.length === 0 ? (
        <EmptyState
          icon="monitor"
          title={loading ? 'Loading…' : 'No egress recorded yet'}
          body="Rows appear once the cdn-cost job has ingested a day of DO Spaces access logs."
        />
      ) : (
        <div className="scroll-x">
          <table className="table-dense">
            <thead>
              <tr>
                <th>Date</th>
                <th>Source</th>
                <th className="num">Egress</th>
                <th className="num">Requests</th>
                <th className="num">Objects</th>
                <th className="num">Est. cost</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {daily.map((d) => (
                <tr key={`${d.reportDate}-${d.source}`}>
                  <td>{d.reportDate}</td>
                  <td>{d.source}</td>
                  <td className="num">{bytes(d.bytesSent)}</td>
                  <td className="num">{d.requestCount.toLocaleString()}</td>
                  <td className="num">{d.uniqueKeys?.toLocaleString() ?? '—'}</td>
                  <td className="num">
                    {usd((d.bytesSent / GB) * (s?.ratePerGb ?? 0))}
                  </td>
                  <td>
                    {/* A live day is partial — labelling it prevents reading a half-day as a drop. */}
                    <span className="muted">{d.isFinal ? 'settled' : 'in progress'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Owner decision e-22: no second control here. This table and Daily egress
          above are the SAME `cdnCostSummary` query — two buttons beside two headings
          read as two independent refreshes of two independent tables, and pressing
          either re-drew both. The one above the first table refreshes this page. */}
      <h2>Heaviest objects</h2>
      <p className="muted">
        Which files are actually burning bandwidth. Settled days keep only their top
        objects, so older rows are the head of the distribution, not everything.
      </p>
      {top.length === 0 ? (
        <EmptyState
          icon="question"
          title={loading ? 'Loading…' : 'No objects recorded yet'}
          body="Per-object totals appear alongside the first ingested day."
        />
      ) : (
        <div className="scroll-x">
          <table className="table-dense">
            <thead>
              <tr>
                <th>Date</th>
                <th>Source</th>
                <th>Object</th>
                <th className="num">Egress</th>
                <th className="num">Requests</th>
                <th className="num">Est. cost</th>
              </tr>
            </thead>
            <tbody>
              {top.map((o) => (
                <tr key={`${o.reportDate}-${o.source}-${o.objectKey}`}>
                  <td>{o.reportDate}</td>
                  <td>{o.source}</td>
                  <td title={o.objectKey}>
                    <code>{o.objectKey.length > 64 ? `${o.objectKey.slice(0, 64)}…` : o.objectKey}</code>
                  </td>
                  <td className="num">{bytes(o.bytesSent)}</td>
                  <td className="num">{o.requestCount.toLocaleString()}</td>
                  <td className="num">{usd((o.bytesSent / GB) * (s?.ratePerGb ?? 0))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
